<template>
  <div class="read-full-wrap">
    <!-- 顶部导航栏：返回按钮 + 阅读进度条 -->
    <div class="top-header-bar">
      <div class="page-back-btn" @click="$router.go(-1)">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#333" stroke-width="2">
          <path d="M19 12H5M12 19l-7-7 7-7"></path>
        </svg>
      </div>
      <div class="legend-bar">
        <span class="legend-item">
          <span class="legend-line over-level"></span> 核心
        </span>
        <span class="legend-item">
          <span class="legend-line over-syllabus"></span> 超纲
        </span>
        <span class="legend-item">
          <span class="legend-line proper"></span> 专有
        </span>
      </div>
      <div class="progress-wrap">
        <div class="progress-bg">
          <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
        </div>
        <span class="progress-text">{{ progressPercent }}%</span>
      </div>
    </div>

    <div class="article-scroll">
      <div class="article-content">
        <div class="title-wrap" style="padding-left: 50px;">
          <h2>{{ article.title }}</h2>
          <div class="collect-btn" :class="{ active: isCollected }" @click="toggleCollect">
            <svg v-if="!isCollected" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#6366f1"
              stroke-width="2">
              <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
            </svg>
            <svg v-else width="22" height="22" viewBox="0 0 24 24" fill="#6366f1">
              <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
            </svg>
            <span>{{ isCollected ? "已收藏" : "收藏" }}</span>
          </div>
        </div>
        <div class="paragraph-wrap" v-for="(para, pIdx) in paragraphs" :key="`para-${pIdx}`">
          <span v-for="(sent, sIdx) in para" :key="`sent-${pIdx}-${sIdx}`"
            :data-sent-idx="getSentenceIndex(pIdx, sIdx)">
            <span v-for="(word, wIdx) in sent.words" :key="`${pIdx}-${sIdx}-${wIdx}-${word.word}`" :class="{
              highlight: isWordHighlight(word),
              'new-word': isNewWord(word),
              'word-over-level': getWordType(word) === 'over_level',
              'word-over-syllabus': getWordType(word) === 'over_syllabus',
              'word-proper': getWordType(word) === 'proper'
            }" @click="handleWordClick(word)">
              {{ word.word }}
              <span v-if="wIdx !== sent.words.length - 1"> </span>
            </span>
            <span v-if="sIdx !== para.length - 1" class="sentence-gap"> </span>
          </span>
        </div>
        <!-- 段落之间的空行 -->
        <div v-if="pIdx !== paragraphs.length - 1" style="height: 20px;"></div>
      </div>
    </div>

    <!-- 底部播放器控制栏 -->
    <div class="player-bar">
      <div class="control-group">
        <div class="study-mode-select">
          <button class="btn-study" :disabled="lockAllControl" @click.stop="showStudyPop = !showStudyPop">
            {{ studyMode === 'listen' ? '聆听模式' : '听读模式' }}
          </button>
          <div class="study-dropdown" v-if="showStudyPop">
            <div class="study-item" @click="switchStudyMode('listen')">聆听模式</div>
            <div class="study-item" @click="switchStudyMode('follow')">听读模式</div>
          </div>
        </div>

        <button class="btn-prev" :disabled="playMode === 'all' || lockAllControl" @click="playPrevSentence">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polygon points="19 20 9 12 19 4 19 20"></polygon>
            <line x1="5" y1="19" x2="5" y2="5"></line>
          </svg>
        </button>
        <button class="btn-play" :disabled="lockAllControl" @click="togglePlay">
          <svg v-if="!isPlaying" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="5 3 19 12 5 21 5 3"></polygon>
          </svg>
          <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="4" width="4" height="16"></rect>
            <rect x="14" y="4" width="4" height="16"></rect>
          </svg>
        </button>
        <button class="btn-next" :disabled="playMode === 'all' || lockAllControl" @click="playNextSentence">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polygon points="5 4 15 12 5 20 5 4"></polygon>
            <line x1="19" y1="5" x2="19" y2="19"></line>
          </svg>
        </button>

        <div class="mode-select">
          <button class="btn-mode" :disabled="lockAllControl || studyMode === 'follow'"
            @click.stop="showModePop = !showModePop">
            {{ playMode === 'single' ? '逐句' : '全文' }}
          </button>
          <div class="mode-dropdown" v-if="showModePop">
            <div class="mode-item" @click="switchMode('single')">逐句</div>
            <div class="mode-item" @click="switchMode('all')">全文</div>
          </div>
        </div>
        <div class="speed-select">
          <button class="btn-speed" :disabled="lockAllControl" @click.stop="showSpeedPop = !showSpeedPop">
            {{ playSpeed === 0.75 ? '慢速' : '常速' }}
          </button>
          <div class="speed-dropdown" v-if="showSpeedPop">
            <div class="speed-item" @click="switchSpeed(0.75)">慢速</div>
            <div class="speed-item" @click="switchSpeed(1)">常速</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 生词释义弹窗 -->
    <div class="word-mask" v-if="showWordPop" @click.self="showWordPop = false">
      <div class="word-pop-box">
        <div class="pop-header">
          <h3 class="pop-word">{{ currentWord.word }}</h3>
          <button class="btn-speak" @click="playWordSound(currentWord.word)" title="点击发音">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 18v-6a9 9 0 0 1 18 0v6"></path>
              <path
                d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h2zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z">
              </path>
            </svg>
          </button>
        </div>
        <div class="pop-phonetic">音标：{{ currentWord.phonetic }}</div>
        <div class="pop-explain">释义：{{ currentWord.desc }}</div>
        <div class="pop-type" v-if="currentWord.type">
          <span class="type-tag" :class="currentWord.type">
            {{ wordTypeLabel(currentWord.type) }}
          </span>
        </div>
        <div class="pop-btns">
          <button class="btn-save" @click="collectWord">加入生词本</button>
          <button class="btn-close" @click="showWordPop = false">关闭</button>
        </div>
      </div>
    </div>

    <div class="follow-mask" v-if="lockAllControl && studyMode === 'follow'">
      <div class="follow-tip">请跟着朗读本句</div>
      <button class="btn-finish-read" @click="finishReadHandler">我读完了</button>
    </div>

    <div class="complete-mask" v-if="showCompleteTip">
      <div class="complete-box">
        <h3>恭喜完成全部朗读！</h3>
        <button @click="resetFollowStudy">重新朗读</button>
        <button @click="$router.go(-1)">返回</button>
      </div>
    </div>
  </div>
</template>

<script>
import { readerFetch } from "@/utils/request";
const buf1 = 0.0005;
const buf2 = 0.22;

export default {
  name: "ArticleRead",
  props: {
    id: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      article: {
        title: "",
        text: "",
        star: 1,
        audioName: "",
        jsonName: "",
        words: [],
      },
      audio: null,
      isPlaying: false,
      playMode: "single",
      playSpeed: 1,
      sentenceList: [],
      currentSentenceIndex: 0,
      audioDuration: 0,
      audioCurrentTime: 0,
      showModePop: false,
      showSpeedPop: false,
      showStudyPop: false,
      timer: null,
      studyMode: "follow",
      lockAllControl: false,
      showCompleteTip: false,
      showWordPop: false,
      currentWord: { word: "", phonetic: "", desc: "", type: "" },
      userCollectIds: [],
      isCollected: false,
      readerId: Number(localStorage.getItem("readerId")) || 0,
      cycleTimer: null,
      cycleStartTs: 0,
      hasActive: false,
      progressPercent: 0,
      currentPlayingIndex: 0,
      cachedProgress: 0,
      wordAudio: null,        // 独立的单词发音实例
      wordPlaying: false,     // 单词是否正在播放
      tipAudio: null,           // 提示音实例
      isTipPlaying: false,      // 提示音是否正在播放
      paragraphs: [],  // 存储分组后的段落
      pIdx: 0,  // 添加这一行
      isSeeking: false,
    };
  },
  async mounted() {
    if (!this.readerId) {
      this.$router.push("/login");
      return;
    }
    try {
      const res = await readerFetch("/article/list");
      const targetId = Number(this.id);
      const targetArticle = res.data.find((item) => Number(item.ID) === targetId);

      if (!targetArticle) {
        alert("短文不存在，返回列表");
        this.$router.push("/read");
        return;
      }
      this.article = { ...targetArticle, words: targetArticle.words || [] };

      const userInfoRes = await readerFetch(`/info?reader_id=${this.readerId}`);
      this.userCollectIds = userInfoRes.data.collectArticleIds || [];
      this.isCollected = this.userCollectIds.includes(Number(this.id));

      if (targetArticle.jsonName) {
        const jsonUrl = `/api/pub/static/${targetArticle.jsonName}`;
        try {
          const jsonRes = await fetch(jsonUrl);
          if (jsonRes.ok) {
            const jsonData = await jsonRes.json();
            // 1. 先加载句子列表
            this.sentenceList = jsonData.segments || [];
            // 2. 紧接着构建段落分组
            this.paragraphs = this.buildParagraphs(this.sentenceList);
          }
        } catch {
          this.sentenceList = [];
          this.paragraphs = [];
        }
      }

      // 3. 对段落内的句子进行多词合并
      if (this.paragraphs.length > 0 && this.article.words.length > 0) {
        this.paragraphs = this.paragraphs.map((para) => {
          return this.mergeMultiWordTokens(para, this.article.words);
        });
        // 同时更新 sentenceList 用于其他逻辑（如高亮）
        this.sentenceList = this.paragraphs.flat();
      }

      if (targetArticle.audioName) {
        const audioUrl = `/api/pub/static/${targetArticle.audioName}`;
        this.initAudio(audioUrl);
      }

      // 初始化提示音
      this.initTipAudio();

      this.startCycleTimer();
      document.addEventListener("visibilitychange", this.handleVisibilityChange);
      window.addEventListener("beforeunload", this.pageLeaveUpload);
    } catch (err) {
      console.error("加载短文失败：", err);
    }
  },
  beforeRouteLeave(to, from, next) {
    this.pageLeaveUpload();
    next();
  },
  beforeDestroy() {
    if (this.audio) {
      this.audio.pause();
      this.audio.src = "";
    }
    if (this.wordAudio) {
      this.wordAudio.pause()
      this.wordAudio.src = ""
      this.wordAudio = null
    }
    // 清除提示音
    if (this.tipAudio) {
      this.tipAudio.onended = null
      this.tipAudio.onerror = null
      this.tipAudio.pause()
      this.tipAudio.src = ''
      this.tipAudio = null
    }
    // 取消所有 setTimeout（如果有）
    if (this._tipTimer) {
      clearTimeout(this._tipTimer)
      this._tipTimer = null
    }
    clearInterval(this.timer);
    this.clearCycleTimer();
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    window.removeEventListener("beforeunload", this.pageLeaveUpload);
  },
  methods: {
    // ---- 工具方法 ----
    getSentenceIndex(pIdx, sIdx) {
      let idx = 0;
      for (let i = 0; i < pIdx; i++) {
        idx += this.paragraphs[i].length;
      }
      return idx + sIdx;
    },
    buildParagraphs(segments) {
      const result = []
      let currentGroup = []
      // 直接删除 let currentPara = 0

      for (const sent of segments) {
        if (sent.para !== undefined && sent.para !== null) {
          if (currentGroup.length > 0) {
            result.push(currentGroup)
            currentGroup = []
          }
          // 直接用 sent.para，不需要 currentPara 变量
        }
        currentGroup.push(sent)
      }
      if (currentGroup.length > 0) {
        result.push(currentGroup)
      }
      if (result.length === 0 && segments.length > 0) {
        result.push([...segments])
      }
      return result
    },
    initTipAudio() {
      // 提示音文件放在 public 目录下，直接用路径引用
      // 或者用 import 方式加载
      this.tipAudio = new Audio('/tip.mp3')  // 路径根据实际情况调整
      this.tipAudio.preload = 'auto'
    },
    playTipSound() {

      if (!this.tipAudio) return
      if (this.isTipPlaying) {
        this.tipAudio.pause()
        this.tipAudio.currentTime = 0
      }

      // ✅ 保存局部引用
      const audio = this.tipAudio

      audio.play().catch((err) => {
        console.warn("提示音播放被Safari拦截", err);
      });

      this.isTipPlaying = true

      // ✅ 用局部变量，不用 this.tipAudio
      audio.onended = () => {
        this.isTipPlaying = false
        audio.onended = null
      }
      audio.onerror = () => {
        this.isTipPlaying = false
        audio.onerror = null
      }
    },
    getWordText(wordObj) {
      return typeof wordObj === 'string' ? wordObj : wordObj.word
    },

    cleanWord(s) {
      if (!s) return ''
      return s.replace(/[,.;!?'"“”‘’]/g, '').toLowerCase()
    },

    // ---- 多词合并 ----
    mergeMultiWordTokens(sentenceList, wordList) {
      const multiWordPhrases = wordList
        .filter(w => w.word.includes(' '))
        .sort((a, b) => b.word.split(' ').length - a.word.split(' ').length)

      return sentenceList.map(sentence => {
        const words = sentence.words
        const newWords = []
        let i = 0

        while (i < words.length) {
          let matched = false
          for (const phrase of multiWordPhrases) {
            const phraseWords = phrase.word.split(' ')
            if (i + phraseWords.length > words.length) continue

            const candidateWords = words.slice(i, i + phraseWords.length)
            const candidate = candidateWords.map(w => this.cleanWord(w.word)).join(' ')
            const phraseClean = this.cleanWord(phrase.word)

            if (candidate === phraseClean) {
              newWords.push({
                word: phrase.word,
                start: candidateWords[0].start,
                end: candidateWords[candidateWords.length - 1].end,
                originalWords: candidateWords
              })
              i += phraseWords.length
              matched = true
              break
            }
          }

          if (!matched) {
            newWords.push(words[i])
            i++
          }
        }

        return { ...sentence, words: newWords }
      })
    },

    // ---- 生词查询 ----
    getTargetWord(wordObj) {
      if (!this.article?.words || !Array.isArray(this.article.words)) return null
      const wordText = this.getWordText(wordObj)
      const cleanedText = this.cleanWord(wordText)
      return this.article.words.find(item => this.cleanWord(item.word) === cleanedText)
    },

    getWordType(wordObj) {
      const entry = this.getTargetWord(wordObj)
      return entry ? entry.type : ''
    },

    isNewWord(wordObj) {
      return !!this.getTargetWord(wordObj)
    },

    wordTypeLabel(type) {
      const map = {
        'over_level': '核心词（必背）',
        'over_syllabus': '超纲词（拓展）',
        'proper': '专有名词（了解）'
      }
      return map[type] || type
    },

    // ---- 核心逻辑 ----
    playWordSound(wordText) {
      if (this.wordPlaying) return

      let targetToken = null
      for (const sent of this.sentenceList) {
        for (const token of sent.words) {
          let wordsToCheck = token.originalWords ? token.originalWords : [token]
          for (const w of wordsToCheck) {
            const clean = (s) => s.replace(/[,.;!?'"“”‘’]/g, '').toLowerCase()
            if (clean(w.word) === clean(wordText)) {
              targetToken = token
              break
            }
          }
          if (targetToken) break
        }
        if (targetToken) break
      }

      if (!targetToken) {
        console.warn('未找到单词时间戳:', wordText)
        return
      }

      let startTime = targetToken.start
      let endTime = targetToken.end
      if (targetToken.originalWords) {
        startTime = targetToken.originalWords[0].start
        endTime = targetToken.originalWords[targetToken.originalWords.length - 1].end
      }

      if (!this.audio || !this.audio.src) {
        console.warn('音频未加载')
        return
      }

      const START_OFFSET = 0.05
      const END_OFFSET = 0.02
      const correctedStart = Math.max(0, startTime - START_OFFSET)
      const correctedEnd = Math.max(correctedStart + 0.05, endTime - END_OFFSET)
      const duration = correctedEnd - correctedStart

      const mainWasPlaying = this.isPlaying
      if (this.isPlaying) {
        this.audio.pause()
      }

      if (this.wordAudio) {
        this.wordAudio.pause()
        this.wordAudio.src = ''
      }

      this.wordAudio = new Audio()
      this.wordAudio.src = this.audio.src
      this.wordAudio.currentTime = correctedStart
      this.wordAudio.playbackRate = 1

      // 清理函数
      const cleanup = () => {
        if (this._wordCheckInterval) {
          clearInterval(this._wordCheckInterval)
          this._wordCheckInterval = null
        }
        if (this._wordStopTimer) {
          clearTimeout(this._wordStopTimer)
          this._wordStopTimer = null
        }
        if (this.wordAudio) {
          this.wordAudio.pause()
          this.wordAudio.src = ''
          this.wordAudio = null
        }
        this.wordPlaying = false
        // 如果之前主播放器在播放，恢复它
        if (mainWasPlaying) {
          // 确保主播放器的 currentTime 没有被修改过
          this.audio.play()
        }
      }

      // 开始播放
      this.wordAudio.play()
      this.wordPlaying = true

      // 方案1：轮询检查 currentTime（主要截断手段）
      // let lastCheckTime = 0
      this._wordCheckInterval = setInterval(() => {
        if (!this.wordAudio || !this.wordPlaying) {
          clearInterval(this._wordCheckInterval)
          this._wordCheckInterval = null
          return
        }
        const current = this.wordAudio.currentTime
        // 如果已经到达或超过终点，立即停止
        if (current >= correctedEnd) {
          cleanup()
        }
        // lastCheckTime = current
      }, 30)

      // 方案2：setTimeout 兜底（防止轮询因某些原因失效）
      const stopDelay = Math.min(duration * 1000 + 300, 4000)
      this._wordStopTimer = setTimeout(() => {
        if (this.wordPlaying) {
          cleanup()
        }
      }, stopDelay)

      // 方案3：ended 事件兜底
      const onEnded = () => {
        if (this.wordPlaying) {
          cleanup()
        }
      }
      this.wordAudio.addEventListener('ended', onEnded)

      // 错误处理
      const onError = () => {
        cleanup()
      }
      this.wordAudio.addEventListener('error', onError)

      console.log('单词:', wordText, '起点:', correctedStart, '终点:', correctedEnd, '时长:', duration)
    },
    initAudio(audioSrc) {
      this.audio = new Audio();
      this.audio.src = audioSrc;
      this.audio.playbackRate = this.playSpeed;
      this.audio.addEventListener("loadedmetadata", () => {
        this.audioDuration = this.audio.duration;
      });
      this.audio.addEventListener("ended", this.handleAudioEnd);

      this.timer = setInterval(() => {
        if (!this.audio) return;
        this.audioCurrentTime = this.audio.currentTime;
        this.updateProgress();
        this.updateCurrentSentenceIndex();
        if (this.isPlaying || this.studyMode === "follow") {
          this.autoScrollToHighlight();
        }

        if (this.playMode === "single" && this.isPlaying && this.studyMode === "follow") {
          const curSent = this.sentenceList[this.currentSentenceIndex];
          if (this.audioCurrentTime >= curSent.end - buf1 * this.audioCurrentTime) {
            this.audio.pause();
            this.isPlaying = false;
            this.lockAllControl = true;
            this._tipTimer = setTimeout(() => {
              this.playTipSound()
            }, 500)
          }
        }
        if (this.playMode === "single" && this.isPlaying && this.studyMode === "listen") {
          const curSent = this.sentenceList[this.currentSentenceIndex];
          if (this.audioCurrentTime >= curSent.end - buf1 * this.audioCurrentTime) {
            this.audio.pause();
            this.isPlaying = false;
          }
        }
      }, 30);
    },

    // ---- 进度条 ----
    updateProgress() {
      if (this.audio && this.audioDuration > 0 && this.isPlaying) {
        this.progressPercent = Number((this.audioCurrentTime / this.audioDuration * 100).toFixed(1));
        this.cachedProgress = this.progressPercent;
        return;
      }
      if (this.playMode === "single" && this.audio && this.audioDuration > 0 && this.audioCurrentTime > 0) {
        this.progressPercent = Number((this.audioCurrentTime / this.audioDuration * 100).toFixed(1));
        this.cachedProgress = this.progressPercent;
        return;
      }
      this.progressPercent = this.cachedProgress || 0;
    },

    // ---- 自动滚动 ----
    updateCurrentSentenceIndex() {
      if (this.playMode === "single") return;
      if (this.playMode === "all" && this.sentenceList.length > 0) {
        const currentTime = this.audioCurrentTime;
        let targetIdx = 0;
        for (let i = 0; i < this.sentenceList.length; i++) {
          const sent = this.sentenceList[i];
          if (currentTime >= sent.start - 0.1 && currentTime < sent.end - 0.1) {
            targetIdx = i;
            break;
          }
          if (currentTime > sent.end - 0.1) {
            targetIdx = i + 1;
          }
        }
        if (targetIdx >= this.sentenceList.length) targetIdx = this.sentenceList.length - 1;
        if (targetIdx < 0) targetIdx = 0;
        if (this.currentPlayingIndex !== targetIdx) {
          this.currentPlayingIndex = targetIdx;
        }
      }
    },

    autoScrollToHighlight() {
      const idx = this.playMode === "all" ? this.currentPlayingIndex : this.currentSentenceIndex;
      if (idx < 0 || idx >= this.sentenceList.length) return;

      const scrollBox = document.querySelector(".article-scroll");
      if (!scrollBox) return;

      // 通过 data-sent-idx 查找当前句子的外层 span
      const allSentenceSpans = scrollBox.querySelectorAll(".paragraph-wrap > span[data-sent-idx]");
      let targetEl = null;
      for (const el of allSentenceSpans) {
        if (parseInt(el.dataset.sentIdx) === idx) {
          targetEl = el;
          break;
        }
      }

      if (!targetEl) return;

      const containerRect = scrollBox.getBoundingClientRect();
      const targetRect = targetEl.getBoundingClientRect();
      const distanceToBottom = containerRect.bottom - targetRect.bottom;
      const threshold = 100;

      if (distanceToBottom < threshold) {
        const halfScreen = containerRect.height / 2;
        const maxScroll = scrollBox.scrollHeight - containerRect.height;
        const currentScroll = scrollBox.scrollTop;
        const targetScroll = Math.min(currentScroll + halfScreen, maxScroll);
        scrollBox.scrollTo({ top: targetScroll, behavior: "smooth" });
      }
    },
    // ---- 高亮 ----
    isWordHighlight(wordObj) {
      if (wordObj.originalWords) {
        return wordObj.originalWords.every(w => w.end <= this.audioCurrentTime + buf1 * this.audioCurrentTime)
      }
      return wordObj.end <= this.audioCurrentTime + buf1 * this.audioCurrentTime
    },

    // ---- 播放控制 ----
    togglePlay() {
      this.markActive();
      if (!this.audio) return;
      if (this.isPlaying) {
        this.audio.pause();
        this.isPlaying = false;
      } else {
        if (this.playMode === "single") {
          const curSent = this.sentenceList[this.currentSentenceIndex];
          if (this.audioCurrentTime >= curSent.end - buf1 * this.audioCurrentTime) {
            const isIOS = /iPad|iPhone|iPod/.test(navigator.platform);
            let backOffset = buf2;
            if (isIOS) {
              backOffset = 0.14;
            }
            const t = Math.max(0, curSent.start - backOffset);
            this.audio.currentTime = t;
          }
        }
        this.audio.play();
        this.isPlaying = true;
      }
    },

    jumpToSentence(targetIdx) {
      if (this.isSeeking) return;

      this.markActive();
      const targetSent = this.sentenceList[targetIdx];
      const isIOS = /iPad|iPhone|iPod/.test(navigator.platform);

      let backOffset = buf2;
      if (isIOS) {
        backOffset = 0.14;
      }
      const seekTime = Math.max(0, targetSent.start - backOffset);

      this.isSeeking = true;
      this.audio.currentTime = seekTime;

      const delayMs = isIOS ? 120 : 0;
      setTimeout(() => {
        try {
          this.audio.play();
          this.isPlaying = true;
          this.$nextTick(() => this.autoScrollToHighlight());
        } catch (e) {
          console.warn("jump play fail", e);
          this.isPlaying = false;
        }
        this.isSeeking = false;
      }, delayMs);
    },

    playPrevSentence() {
      if (this.playMode !== "single" || this.currentSentenceIndex <= 0) return;
      this.audio.pause();
      this.currentSentenceIndex--;
      this.jumpToSentence(this.currentSentenceIndex);
    },

    playNextSentence() {
      if (this.playMode !== "single" || this.currentSentenceIndex >= this.sentenceList.length - 1) return;
      this.audio.pause();
      this.currentSentenceIndex++;
      this.jumpToSentence(this.currentSentenceIndex);
    },

    handleAudioEnd() {
      this.isPlaying = false;
      this.isSeeking = false;
      if (this.playMode === "all") {
        this.audio.currentTime = 0;
        this.currentSentenceIndex = 0;
        this.currentPlayingIndex = 0;
      }
    },

    // ---- 模式切换 ----
    switchMode(mode) {
      if (this.studyMode === "follow") return;
      this.playMode = mode;
      this.showModePop = false;
      this.resetAudioState();
    },

    switchSpeed(speed) {
      this.playSpeed = speed;
      this.showSpeedPop = false;
      if (this.audio) this.audio.playbackRate = speed;
    },

    switchStudyMode(mode) {
      this.studyMode = mode;
      this.showStudyPop = false;
      this.resetAudioState();
      if (this.studyMode === "follow") this.playMode = "single";
    },

    resetAudioState() {
      if (this.audio) {
        this.audio.pause();
        this.audio.currentTime = 0;
      }
      this.isPlaying = false;
      this.currentSentenceIndex = 0;
      this.currentPlayingIndex = 0;
      this.lockAllControl = false;
      this.showCompleteTip = false;
      this.isSeeking = false;
    },
    // ---- 跟读模式 ----
    finishReadHandler() {
      this.markActive();
      this.lockAllControl = false;
      if (this.currentSentenceIndex >= this.sentenceList.length - 1) {
        this.showCompleteTip = true;
        return;
      }
      if (this.isSeeking) return;

      this.currentSentenceIndex++;
      setTimeout(() => {
        this.jumpToSentence(this.currentSentenceIndex);
      }, 1200);
    },

    resetFollowStudy() {
      this.markActive();
      this.showCompleteTip = false;
      this.currentSentenceIndex = 0;
      this.currentPlayingIndex = 0;
      this.lockAllControl = false;
      this.jumpToSentence(0);
    },

    // ---- 生词点击 ----
    handleWordClick(wordObj) {
      this.markActive()
      const matchWord = this.getTargetWord(wordObj)
      if (!matchWord) return
      this.currentWord = { ...matchWord }
      this.showWordPop = true
    },

    async collectWord() {
      try {
        const res = await readerFetch("/word/add", {
          method: "POST",
          body: {
            readerId: this.readerId,
            word: this.currentWord.word,
            phonetic: this.currentWord.phonetic,
            desc: this.currentWord.desc,
          },
        });
        alert(res.code === 200 ? "已添加至生词本！" : res.msg || "添加失败");
      } catch {
        alert("网络异常，添加失败");
      }
      this.showWordPop = false;
    },

    // ---- 收藏 ----
    async toggleCollect() {
      this.markActive();
      const aid = Number(this.id);
      if (this.isCollected) {
        await readerFetch("/uncollect", {
          method: "POST",
          body: { userId: this.readerId, articleId: aid },
        });
        this.userCollectIds = this.userCollectIds.filter((id) => id !== aid);
        this.isCollected = false;
      } else {
        await readerFetch("/collect", {
          method: "POST",
          body: { userId: this.readerId, articleId: aid },
        });
        this.userCollectIds.push(aid);
        this.isCollected = true;
      }
    },

    // ---- 时长上报 ----
    markActive() {
      this.hasActive = true;
    },

    startCycleTimer() {
      this.cycleStartTs = Date.now();
      this.hasActive = false;
      clearInterval(this.cycleTimer);
      this.cycleTimer = setInterval(() => {
        if (Date.now() - this.cycleStartTs >= 28000) {
          this.cycleSettle();
        }
      }, 30000);
    },

    async cycleSettle() {
      if (this.hasActive) {
        await this.uploadReadTime();
      }
      this.hasActive = false;
      this.cycleStartTs = Date.now();
    },

    clearCycleTimer() {
      clearInterval(this.cycleTimer);
      this.cycleTimer = null;
    },

    handleVisibilityChange() {
      if (document.hidden) {
        // 切后台立刻取消未完成的阅读计时，不足30秒不计入
        this.clearCycleTimer();
        if (this.hasActive) this.uploadReadTime();
        this.hasActive = false;
      } else {
        this.startCycleTimer();
      }
    },

    pageLeaveUpload() {
      this.clearCycleTimer();
      if (this.hasActive) this.uploadReadTime();
    },

    async uploadReadTime() {
      try {
        await readerFetch("/read/upload", {
          method: "POST",
          body: {
            readerId: this.readerId,
            articleId: Number(this.id),
            addSec: 30,
          },
        });
      } catch (e) {
        console.log("时长上报失败", e);
      }
    },
  },
};
</script>

<style scoped>
.read-full-wrap {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #ffffffe5;
  z-index: 999;
  overflow: hidden;
  touch-action: none;
}

/* ===== 顶部导航 ===== */
.top-header-bar {
  position: fixed;
  top: env(safe-area-inset-top, 20px);
  left: 0;
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  box-sizing: border-box;
  z-index: 9999;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(4px);
}

.page-back-btn {
  flex-shrink: 0;
  padding: 6px;
  cursor: pointer;
}

.progress-wrap {
  width: 30vw;
  min-width: 120px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.progress-bg {
  flex: 1;
  height: 10px;
  background: #c9f1ffaa;
  border-radius: 99px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #eda0f1aa;
  border-radius: 99px;
  transition: width 0.1s linear;
}

.progress-text {
  font-size: 12px;
  color: #999;
  width: 32px;
  text-align: right;
  flex-shrink: 0;
}

/* ===== 图例 ===== */
.legend-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 12px;
  color: #888;
  flex: 1;
  justify-content: center;
  min-width: 0;
  padding: 0 8px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-line {
  display: inline-block;
  width: 18px;
  height: 0;
  border-radius: 2px;
}

.legend-line.over-level {
  border-top: 2px dashed #e6a800;
}

.legend-line.over-syllabus {
  border-top: 2px solid #2563eb;
}

.legend-line.proper {
  border-top: 2px dotted #9ca3af;
}

/* ===== 滚动区 ===== */
.article-scroll {
  width: 100%;
  height: calc(100% - 75px);
  overflow-y: auto;
  overflow-x: hidden;
  margin-top: 75px;
  padding: 1px 120px calc(130px + env(safe-area-inset-bottom));
  box-sizing: border-box;
  overscroll-behavior-y: contain;
  touch-action: pan-y;
}

.article-content {
  max-width: 780px;
  margin: 0 auto;
}

.title-wrap {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
}

.article-content h2 {
  font-size: 26px;
  font-weight: 700;
  font-style: italic;
  letter-spacing: 0.7px;
  color: #1a1a1a;
}

.collect-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #6366f1;
  cursor: pointer;
  font-size: 14px;
}

.collect-btn.active {
  color: #6366f1;
}

/* ===== 正文 ===== */
.paragraph-wrap {
  font-size: 25px;
  line-height: 1.8;
  color: #000000;
  margin-bottom: 24px;
  font-family: "Times New Roman", serif;
  text-align: justify;
}

.sentence-gap {
  display: inline-block;
  width: 0.3em;
}

.sentence-wrap {
  font-size: 25px;
  line-height: 1.9;
  color: #000000;
  margin-bottom: 24px;
  font-family: "Times New Roman", serif;
  text-align: left;
}

.sentence-wrap span {
  display: inline;
  cursor: default;
  letter-spacing: 0.2px;
}

/* ===== 高亮（播放进度） ===== */
.highlight {
  color: #22bb00;
  font-weight: 500;
}

/* ===== 三色下划线 ===== */
.word-over-level {
  text-decoration: underline;
  text-decoration-style: dashed;
  text-decoration-color: #e6a800;
  text-underline-offset: 2px;
  cursor: pointer !important;
}

.word-over-syllabus {
  text-decoration: underline;
  text-decoration-style: solid;
  text-decoration-color: #2563eb;
  text-underline-offset: 2px;
  cursor: pointer !important;
}

.word-proper {
  text-decoration: underline;
  text-decoration-style: dotted;
  text-decoration-color: #9ca3af;
  text-underline-offset: 2px;
  cursor: pointer !important;
}

/* 兼容旧版 new-word（保留蓝色下划线作为 fallback） */
.new-word {
  text-decoration: underline;
  text-decoration-color: #004499aa;
  text-underline-offset: 1px;
  cursor: pointer !important;
}

/* 如果同时有 new-word 和三色，三色优先覆盖 */
.word-over-level.new-word {
  text-decoration-color: #e6a800 !important;
  text-decoration-style: dashed !important;
}

.word-over-syllabus.new-word {
  text-decoration-color: #2563eb !important;
  text-decoration-style: solid !important;
}

.word-proper.new-word {
  text-decoration-color: #9ca3af !important;
  text-decoration-style: dotted !important;
}

/* ===== 弹窗 ===== */
.word-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.45);
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.word-pop-box {
  background: #fff;
  padding: 26px 32px;
  border-radius: 12px;
  min-width: 320px;
  border: 1px solid #ddd;
}

.pop-word {
  font-size: 26px;
  color: #000;
  margin-bottom: 10px;
  font-family: "Times New Roman";
}

.pop-phonetic {
  color: #004499;
  font-size: 15px;
  margin-bottom: 10px;
}

.pop-explain {
  font-size: 16px;
  color: #222;
  margin-bottom: 22px;
  line-height: 1.6;
}

.pop-type {
  margin-bottom: 16px;
}

.type-tag {
  display: inline-block;
  padding: 2px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.type-tag.over_level {
  background: #fef3c7;
  color: #92400e;
  border: 1px dashed #e6a800;
}

.type-tag.over_syllabus {
  background: #dbeafe;
  color: #1e40af;
  border: 1px solid #2563eb;
}

.type-tag.proper {
  background: #f3f4f6;
  color: #6b7280;
  border: 1px dotted #9ca3af;
}

.pop-btns {
  display: flex;
  gap: 14px;
  justify-content: flex-end;
}

.btn-save {
  padding: 7px 18px;
  background: #004499;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.btn-close {
  padding: 7px 18px;
  background: #e5e7eb;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

/* ===== 底部播放器 ===== */
.player-bar {
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 70px;
  background: #ffffff;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  border-top: 1px solid #eee;
}

.control-group {
  display: flex;
  align-items: center;
  gap: 24px;
}

.btn-prev,
.btn-next {
  background: transparent;
  border: none;
  color: #004499;
  cursor: pointer;
  padding: 4px;
}

.btn-prev:disabled,
.btn-next:disabled {
  color: #cbd5e1;
  cursor: not-allowed;
}

.btn-play {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #004499;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s;
}

.btn-play:disabled {
  background: #cbd5e1;
  cursor: not-allowed;
}

.btn-play:hover:not(:disabled) {
  transform: scale(1.05);
}

.mode-select,
.speed-select,
.study-mode-select {
  position: relative;
}

.btn-mode,
.btn-speed,
.btn-study {
  padding: 5px 12px;
  border: 1px solid #004499;
  border-radius: 18px;
  background: #fff;
  color: #004499;
  font-size: 13px;
  cursor: pointer;
}

.btn-mode:disabled,
.btn-speed:disabled,
.btn-study:disabled {
  border-color: #cbd5e1;
  color: #cbd5e1;
  cursor: not-allowed;
}

.mode-dropdown,
.speed-dropdown,
.study-dropdown {
  position: absolute;
  bottom: 55px;
  right: 0;
  width: 86px;
  background: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #eee;
}

.mode-item,
.speed-item,
.study-item {
  padding: 9px 0;
  text-align: center;
  font-size: 14px;
  cursor: pointer;
}

.mode-item:hover,
.speed-item:hover,
.study-item:hover {
  background: #f0f6ff;
}

/* ===== 跟读遮罩 ===== */
.follow-mask {
  position: fixed;
  inset: 0;
  z-index: 9998;
  background: #30201040;
  display: flex;
  flex-direction: row;
  align-items: end;
  justify-content: left;
  gap: 5px;
}

.follow-tip {
  margin: 10px 10px;
  font-size: 15px;
  color: #004499;
}

.btn-finish-read {
  padding: 5px 10px;
  background: #004499;
  color: white;
  border-radius: 999px;
  font-size: 20px;
  border: none;
  margin-bottom: 10px;
}

/* ===== 完成弹窗 ===== */
.complete-mask {
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.complete-box {
  background: white;
  padding: 40px 60px;
  border-radius: 12px;
  text-align: center;
  border: 1px solid #ddd;
}

.complete-box h3 {
  font-size: 22px;
  margin-bottom: 24px;
  color: #000;
  font-family: "SimSun";
}

.complete-box button {
  padding: 8px 24px;
  border-radius: 20px;
  border: none;
  background: #004499;
  color: #fff;
  cursor: pointer;
}

/* ===== 移动端适配 ===== */
@media screen and (max-width: 600px) {
  .control-group {
    gap: 14px;
  }

  .btn-play {
    width: 36px;
    height: 36px;
  }

  .player-bar {
    height: 64px;
  }

  .article-scroll {
    padding: 48px 45px calc(180px + env(safe-area-inset-bottom));
  }

  .sentence-wrap {
    font-size: 16px;
    line-height: 1.7;
  }

  .paragraph-wrap {
    font-size: 16px;
    line-height: 1.7;
  }

  .btn-mode,
  .btn-speed,
  .btn-study {
    padding: 4px 10px;
    font-size: 12px;
  }

  .follow-tip {
    font-size: 18px;
  }

  .btn-finish-read {
    padding: 12px 30px;
    font-size: 16px;
  }

  .word-pop-box {
    min-width: 280px;
    padding: 20px 24px;
  }

  .top-header-bar {
    height: 44px;
    padding: 0 8px;
  }


  .progress-text {
    width: 32px;
    font-size: 11px;
  }

  .legend-bar {
    gap: 8px;
    font-size: 10px;
  }

  .legend-line {
    width: 14px;
  }
}

.progress-wrap {
  width: 25vw;
  min-width: 80px;
  gap: 4px;
}

.progress-text {
  font-size: 10px;
  width: 28px;
}

.page-back-btn svg {
  width: 20px;
  height: 20px;
}

.pop-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.btn-speak {
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 50%;
  color: #004499;
  transition: background 0.2s;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-speak:hover {
  background: #f0f6ff;
}

.btn-speak:active {
  transform: scale(0.95);
}
</style>